import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { useOrdersQuery } from '#/api/queries/orderQueries';
import { useWalletWithPositionsQuery } from '#/api/queries/walletPositionsQueries';
import { useSelectorActiveChain } from '#/components/hooks';
import { useCancelOrder } from '#/components/orders/cancelOrder';
import { useSelectMarketTickSize } from '#/reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from '#/reduxState/store';

import { ChartComponentCore } from './ChartComponentCore';
import { useUpdateOrderPrice } from './drawings/orderLinesFunctions';
import { usePositionTPSLHandlers } from './drawings/positionTPSLFunctions';

const Wrap = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const ChartWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: auto;

  iframe {
    opacity: 0;
    transition: 1s opacity ease;
  }
`;

/** Prevent white flash while loading iframe. charting_library.js must be modified, see README */
(window as any).onIframeChartLoad = function () {
  this.style.opacity = 1;
};

function ChartComponent() {
  const wallet = useSelector(state => state.wallet);
  const isMobile = useSelector(state => state.screen.isMobile);
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const { activeChain } = useSelectorActiveChain();
  const tickSize = useSelectMarketTickSize(currentMarket.pair);

  const { walletAddress } = wallet;

  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );

  const { data: ordersList } = useOrdersQuery();
  const { data: walletWithPositions } = useWalletWithPositionsQuery(!isMobile);

  const { handleCancelOrder, renderCancelation } = useCancelOrder();

  const currentMarketPosition = isMobile
    ? undefined
    : walletWithPositions?.positions
    ? walletWithPositions.positions.find(
        position => position.market === currentMarket.pair
      )
    : false;

  const currentMarketOrders = useMemo(
    () =>
      ordersList
        ? ordersList.filter(order => order.market === currentMarket.pair)
        : false,
    [currentMarket.pair, ordersList]
  );

  /** Open modal to update TP/SL for position rendered on the chart */
  const { tpSlModalPlaceholder, handleEditTpSL } = usePositionTPSLHandlers({
    currentMarketPosition,
  });

  /** Update Order price with optional Warning Modal for instant execution, and Error Modals handling */
  const { confirmationOrderUpdatePlaceholder, handleUpdateOrderPrice } =
    useUpdateOrderPrice({
      currentMarket: currentMarket.pair,
      currentMarketPosition,
    });

  const onReady = useCallback(() => console.log('[TradingView] Ready'), []);

  const maximumPositionSize = currentMarket.pair
    ? maximumPositionSizes[currentMarket.pair]
    : undefined;

  return (
    <Wrap>
      {renderCancelation()}
      {confirmationOrderUpdatePlaceholder}
      {tpSlModalPlaceholder}

      <ChartWrap>
        {currentMarket.base &&
        currentMarket.quote &&
        tickSize &&
        maximumPositionSize &&
        // Wait for user orders/positions if unlocked
        (!walletAddress ||
          (currentMarketOrders !== false &&
            currentMarketPosition !== false)) ? (
          <ChartComponentCore
            baseSymbol={currentMarket.base}
            quoteSymbol={currentMarket.quote}
            nightMode
            activeChain={activeChain}
            isMobile={isMobile}
            onReady={onReady}
            priceDecimals={tickSize.visibleDecimals}
            ordersList={currentMarketOrders || undefined}
            position={currentMarketPosition || undefined}
            stepSizes={stepSizes}
            tickSizes={tickSizes}
            onCancelOrder={handleCancelOrder}
            onChangeTPSLPrice={handleEditTpSL}
            onChangeOrderPrice={handleUpdateOrderPrice}
            maximumPositionSize={maximumPositionSize}
          />
        ) : null}
      </ChartWrap>
    </Wrap>
  );
}

export default ChartComponent;

// Use this code to set static height in case Chart auto size not working properly.

// const [height, setHeight] = useState<number | undefined>();
// const ref = useRef<HTMLDivElement>(null);
// const disabledAdjustingSize = useRef(false);
// const adjustHeightWhenNeed = () => {
//   if (ref.current && disabledAdjustingSize.current !== true) {
//     const clientHeight = ref.current.clientHeight || 300;
//     if (clientHeight !== height) {
//       setHeight(ref.current.clientHeight);
//     }
//   }
// };
// useEffect(() => {
//   adjustHeightWhenNeed();
// }, []);

// useEffect(() => {
//   // We want to disable resizing iframe when fullscreen was clicked
//   const onChangeFs = () => {
//     disabledAdjustingSize.current = !!document.fullscreenElement;
//   };
//   document.addEventListener('fullscreenchange', onChangeFs);
//   return () => {
//     document.removeEventListener('fullscreenchange', onChangeFs);
//   };
// });
// // Missing: proper throttle onResize
// // Note that this change trigger whole chart rerender
// useInterval(() => {
//   adjustHeightWhenNeed();
// }, 2000);
